///
/// EarthBrowser
///
/// Polygon Ring Simplification
///
/// Reduces vertex counts of GeoJSON perimeter rings before triangulation
/// and outline construction. NIFC perimeters are surveyed at meter-level
/// detail (vertices every few meters); at the zooms where perimeters render,
/// tolerances of tens of meters are visually indistinguishable and typically
/// cut vertex counts by 5-10x.
///
/// Radial-distance decimation pre-pass followed by Douglas-Peucker, working
/// in locally-scaled degrees (longitude compressed by cos latitude).
///
/// Created by Matt Giger on 9/10/2026
/// Copyright (c) 2026 Geodata Labs
///

const cMetersPerDegree = 111_320

// A closed GeoJSON ring needs 4 points minimum (3 distinct + closing dup);
// below 5 the simplification isn't worth the risk of degeneracy
const cMinRingPoints = 5

/**
 * Simplify a closed GeoJSON ring (array of [lon, lat], first == last).
 * Endpoints are preserved, so the ring stays closed. Returns the original
 * ring when it's already small or would collapse below a valid polygon.
 */
export function simplifyRing(ring: number[][], toleranceMeters: number): number[][] {
	if (ring.length <= cMinRingPoints) return ring

	const lonScale = Math.max(0.01, Math.cos(ring[0][1] * Math.PI / 180))
	const tolDeg = toleranceMeters / cMetersPerDegree
	const tolSq = tolDeg * tolDeg

	// Project to locally uniform degrees
	const xs = new Float64Array(ring.length)
	const ys = new Float64Array(ring.length)
	for (let i = 0; i < ring.length; i++) {
		xs[i] = ring[i][0] * lonScale
		ys[i] = ring[i][1]
	}

	// Radial-distance pre-pass: drop runs of near-coincident survey points
	const kept: number[] = [0]
	let lastX = xs[0]
	let lastY = ys[0]
	for (let i = 1; i < ring.length - 1; i++) {
		const dx = xs[i] - lastX
		const dy = ys[i] - lastY
		if (dx * dx + dy * dy > tolSq) {
			kept.push(i)
			lastX = xs[i]
			lastY = ys[i]
		}
	}
	kept.push(ring.length - 1)

	// Douglas-Peucker on the surviving points
	const keep = new Uint8Array(kept.length)
	keep[0] = 1
	keep[kept.length - 1] = 1
	const stack: Array<[number, number]> = [[0, kept.length - 1]]

	while (stack.length > 0) {
		const [a, b] = stack.pop()!
		if (b - a < 2) continue

		const ax = xs[kept[a]]
		const ay = ys[kept[a]]
		const bx = xs[kept[b]]
		const by = ys[kept[b]]
		const abx = bx - ax
		const aby = by - ay
		const abLenSq = abx * abx + aby * aby

		let maxDistSq = -1
		let maxI = -1
		for (let i = a + 1; i < b; i++) {
			const px = xs[kept[i]]
			const py = ys[kept[i]]
			let distSq: number
			if (abLenSq < 1e-24) {
				// Degenerate segment (e.g. the closing point of the ring):
				// fall back to point distance
				const dx = px - ax
				const dy = py - ay
				distSq = dx * dx + dy * dy
			} else {
				const cross = abx * (py - ay) - aby * (px - ax)
				distSq = (cross * cross) / abLenSq
			}
			if (distSq > maxDistSq) {
				maxDistSq = distSq
				maxI = i
			}
		}

		if (maxDistSq > tolSq) {
			keep[maxI] = 1
			stack.push([a, maxI], [maxI, b])
		}
	}

	const out: number[][] = []
	for (let i = 0; i < kept.length; i++) {
		if (keep[i]) out.push(ring[kept[i]])
	}
	return out.length >= cMinRingPoints ? out : ring
}
